/**
 * CampaignForm — create/edit dialog contents for campaigns (Story 7.5).
 *
 * Sections:
 *   - Basics: name + description + active toggle (edit only)
 *   - Products: basic + premium selects; client-side diff check
 *   - Content: template select + prompt select + KB textarea
 *   - Timing: trigger delay + eligibility window + ActiveHoursInput
 *   - Kill Switches: 4 switches (AC2/AC4 semantics)
 *   - Advanced (collapsible): OpenAI overrides
 *
 * Validation is a layered approach — TypeBox guards the wire, the server
 * runs cross-FK checks inside the tx, and the form only blocks submission on
 * issues that it can detect without a server round-trip (empty name,
 * identical products, invalid hours). The form NEVER re-enables a kill
 * switch that was explicitly turned off — the server won't either (AC4).
 */

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiClient } from '@/lib/api';
import {
  ActiveHoursInput,
  DEFAULT_ACTIVE_HOURS,
  type ActiveHoursValue,
} from '@/components/campaigns/ActiveHoursInput';
import {
  KillSwitchToggles,
  KILL_SWITCH_DEFAULT,
  type KillSwitchValue,
} from '@/components/campaigns/KillSwitchToggles';

export interface CampaignFormValue {
  name: string;
  description: string | null;
  basicProductId: string;
  premiumProductId: string;
  triggerDelayHours: number;
  eligibilityWindowDays: number;
  activeHours: ActiveHoursValue;
  templateId: string | null;
  promptId: string | null;
  kbText: string | null;
  active: boolean;
  killSwitches: KillSwitchValue;
  openaiModel: string | null;
  openaiTemperature: number | null;
  openaiMaxTokens: number | null;
}

export const DEFAULT_CAMPAIGN_FORM: CampaignFormValue = {
  name: '',
  description: null,
  basicProductId: '',
  premiumProductId: '',
  triggerDelayHours: 168,
  eligibilityWindowDays: 90,
  activeHours: DEFAULT_ACTIVE_HOURS,
  templateId: null,
  promptId: null,
  kbText: null,
  active: false,
  killSwitches: KILL_SWITCH_DEFAULT,
  openaiModel: null,
  openaiTemperature: null,
  openaiMaxTokens: null,
};

/** Wire shape for a product option (narrow projection). */
interface ProductOption {
  id: string;
  name: string;
  tier: 'basic' | 'premium';
  active: boolean;
}

/** Wire shape for a template option (narrow projection). */
interface TemplateOption {
  id: string;
  metaTemplateName: string;
  active: boolean;
  /** Story 7.27b — read by the dropdown filter (approved-only). */
  status: 'pending' | 'approved' | 'rejected' | 'paused';
}

/** Wire shape for a prompt option (narrow projection). */
interface PromptOption {
  id: string;
  name: string;
  activeVersionId: string | null;
}

interface Props {
  mode: 'create' | 'edit';
  initial?: CampaignFormValue;
  submitting?: boolean;
  onSubmit: (next: CampaignFormValue) => void;
  onCancel: () => void;
}

export function CampaignForm({ mode, initial, submitting, onSubmit, onCancel }: Props) {
  const [form, setForm] = useState<CampaignFormValue>(initial ?? DEFAULT_CAMPAIGN_FORM);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [activeHoursError, setActiveHoursError] = useState<string | null>(null);

  // Sync when `initial` changes (editing a different campaign).
  useEffect(() => {
    if (initial) setForm(initial);
  }, [initial]);

  const allProductsQuery = useQuery({
    queryKey: ['admin', 'products', 'all-for-campaign'],
    queryFn: () => apiClient.get<{ data: ProductOption[] }>('/admin/products?pageSize=100'),
  });

  const templatesQuery = useQuery({
    queryKey: ['admin', 'templates', 'for-campaign'],
    queryFn: () => apiClient.get<{ data: TemplateOption[] }>('/admin/templates?pageSize=100'),
  });

  const promptsQuery = useQuery({
    queryKey: ['admin', 'prompts', 'for-campaign'],
    queryFn: () => apiClient.get<{ data: PromptOption[] }>('/admin/prompts'),
  });

  const sameProductError = useMemo(() => {
    if (
      form.basicProductId &&
      form.premiumProductId &&
      form.basicProductId === form.premiumProductId
    ) {
      return 'Básico e premium devem ser produtos diferentes.';
    }
    return null;
  }, [form.basicProductId, form.premiumProductId]);

  function patch<K extends keyof CampaignFormValue>(key: K, value: CampaignFormValue[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function canSubmit(): boolean {
    if (!form.name.trim()) return false;
    // Both products are optional. basicProductId is the trigger a webhook
    // resolves the campaign by; premiumProductId is what the AI upsells.
    // A campaign reachable only via the generic Bearer trigger webhook
    // (campaignId passed directly) needs neither.
    if (sameProductError) return false;
    if (activeHoursError) return false;
    return true;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit()) return;
    onSubmit(form);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {/* ── Basics ──────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Básico
        </h3>
        <div className="space-y-1">
          <Label htmlFor="campaign-name">Nome</Label>
          <Input
            id="campaign-name"
            value={form.name}
            disabled={submitting}
            onChange={(e) => patch('name', e.target.value)}
            placeholder="Ex.: Upsell Básico → Premium (Q2)"
            required
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="campaign-description">Descrição</Label>
          <textarea
            id="campaign-description"
            className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={form.description ?? ''}
            disabled={submitting}
            onChange={(e) => patch('description', e.target.value || null)}
            placeholder="Contexto, segmento, objetivo…"
          />
        </div>
        {mode === 'edit' && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              disabled={submitting}
              onChange={(e) => patch('active', e.target.checked)}
              aria-label="Campanha ativa"
            />
            Campanha ativa
          </label>
        )}
      </section>

      {/* ── Products ────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Produtos
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="campaign-basic-product">Produto básico (gatilho, opcional)</Label>
            <select
              id="campaign-basic-product"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.basicProductId}
              disabled={submitting}
              onChange={(e) => patch('basicProductId', e.target.value)}
            >
              <option value="">Nenhum (campanha só via webhook genérico)</option>
              {allProductsQuery.data?.data.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.active}>
                  {p.name}
                  {p.active ? '' : ' (inativo)'}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Opcional — produto cuja compra dispara a campanha via webhook (Guru, batch
              retroactive). Deixe vazio se a campanha só é iniciada via integração externa passando
              o campaignId direto (CRM, n8n, webhook genérico de trigger).
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="campaign-premium-product">Produto premium (opcional)</Label>
            <select
              id="campaign-premium-product"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.premiumProductId}
              disabled={submitting}
              onChange={(e) => patch('premiumProductId', e.target.value)}
            >
              <option value="">Nenhum (campanha de recuperação/reengajamento)</option>
              {allProductsQuery.data?.data.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.active}>
                  {p.name}
                  {p.active ? '' : ' (inativo)'}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Opcional — produto que a Rafaela tenta vender/upsell. Deixe vazio se a campanha só
              reengaja o cliente sem empurrar um produto específico (ex.: recuperação).
            </p>
          </div>
        </div>
        {sameProductError ? (
          <p role="alert" className="text-xs text-destructive">
            {sameProductError}
          </p>
        ) : null}
      </section>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Conteúdo
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="campaign-template">Template Meta</Label>
            <select
              id="campaign-template"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.templateId ?? ''}
              disabled={submitting}
              onChange={(e) => patch('templateId', e.target.value || null)}
            >
              <option value="">Nenhum</option>
              {/* Story 7.27b — listamos só APPROVED. Templates em pending/
                  rejected/paused não podem ser usados em send (Meta rejeita).
                  Esconder evita o operador tentar selecionar um que não vai
                  funcionar. Exceção: o template já selecionado na campanha
                  fica visível mesmo se mudou de status (com sufixo de aviso),
                  para o operador não perder o vínculo silenciosamente. */}
              {templatesQuery.data?.data
                .filter((t) => t.status === 'approved' || t.id === form.templateId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.metaTemplateName}
                    {t.status !== 'approved' ? ` (${t.status} — escolha outro)` : ''}
                  </option>
                ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="campaign-prompt">Prompt</Label>
            <select
              id="campaign-prompt"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.promptId ?? ''}
              disabled={submitting}
              onChange={(e) => patch('promptId', e.target.value || null)}
            >
              <option value="">Nenhum</option>
              {promptsQuery.data?.data.map((p) => (
                <option key={p.id} value={p.id} disabled={!p.activeVersionId}>
                  {p.name}
                  {p.activeVersionId ? '' : ' (sem versão ativa)'}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="campaign-kb-text">Knowledge Base (texto livre)</Label>
          <textarea
            id="campaign-kb-text"
            className="flex min-h-[110px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={form.kbText ?? ''}
            disabled={submitting}
            onChange={(e) => patch('kbText', e.target.value || null)}
            placeholder="Contexto que o agente precisa consultar…"
          />
        </div>
      </section>

      {/* ── Timing ──────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Timing
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="campaign-trigger-delay">Trigger delay (horas)</Label>
            <Input
              id="campaign-trigger-delay"
              type="number"
              min={1}
              max={8760}
              value={form.triggerDelayHours}
              disabled={submitting}
              onChange={(e) => patch('triggerDelayHours', Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">Entre 1 e 8760 (1 ano).</p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="campaign-eligibility-window">Janela de elegibilidade (dias)</Label>
            <Input
              id="campaign-eligibility-window"
              type="number"
              min={1}
              max={365}
              value={form.eligibilityWindowDays}
              disabled={submitting}
              onChange={(e) => patch('eligibilityWindowDays', Number(e.target.value))}
            />
            <p className="text-xs text-muted-foreground">Entre 1 e 365.</p>
          </div>
        </div>
        <ActiveHoursInput
          value={form.activeHours}
          onChange={(next) => patch('activeHours', next)}
          onValidityChange={setActiveHoursError}
          disabled={submitting}
        />
      </section>

      {/* ── Kill Switches ───────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Kill switches
        </h3>
        <KillSwitchToggles
          value={form.killSwitches}
          onChange={(next) => patch('killSwitches', next)}
          disabled={submitting}
        />
        <p className="text-xs text-muted-foreground">
          Desligar um switch nunca é desfeito automaticamente. Reativação é sempre manual.
        </p>
      </section>

      {/* ── Advanced ────────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <button
          type="button"
          className="text-sm font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
          onClick={() => setAdvancedOpen((o) => !o)}
          aria-expanded={advancedOpen}
        >
          {advancedOpen ? '▼' : '▶'} Avançado (OpenAI overrides)
        </button>
        {advancedOpen && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="openai-model">Modelo</Label>
              <Input
                id="openai-model"
                value={form.openaiModel ?? ''}
                disabled={submitting}
                onChange={(e) => patch('openaiModel', e.target.value || null)}
                placeholder="gpt-4o-mini"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="openai-temperature">Temperature</Label>
              <Input
                id="openai-temperature"
                type="number"
                step={0.01}
                min={0}
                max={2}
                value={form.openaiTemperature ?? ''}
                disabled={submitting}
                onChange={(e) =>
                  patch('openaiTemperature', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="0.70"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="openai-max-tokens">Max tokens</Label>
              <Input
                id="openai-max-tokens"
                type="number"
                min={50}
                max={2000}
                value={form.openaiMaxTokens ?? ''}
                disabled={submitting}
                onChange={(e) =>
                  patch('openaiMaxTokens', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="500"
              />
            </div>
          </div>
        )}
      </section>

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={submitting || !canSubmit()}>
          {submitting ? 'Salvando…' : mode === 'create' ? 'Criar campanha' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}

export default CampaignForm;
